import { useCallback, useEffect, useRef, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";

export type HubTab = {
  path: string;
  label: string;
  /** Exact-match only — needed for a tab that sits on the hub's base path so it
   *  doesn't stay active while a sibling sub-tab is open. */
  end?: boolean;
};

/** Sub-tab sets for the grouped admin hub pages. Each sub-tab is its own nested
 *  route so it stays deep-linkable, and the sidebar hub entry keeps highlighting
 *  via its path prefix. */
export const HUB_TABS = {
  insights: [
    { path: "/admin/insights/dashboard", label: "Dashboard" },
    { path: "/admin/insights/analytics", label: "Analytics" },
    { path: "/admin/insights/activity", label: "Activity" },
    { path: "/admin/insights/ai-generations", label: "AI Generations" },
    { path: "/admin/insights/market-pulse", label: "Market Pulse" },
    { path: "/admin/insights/rag-chat", label: "RAG Chat" },
    { path: "/admin/insights/market-research", label: "Market Research" },
  ],
  deals: [
    { path: "/admin/deals", label: "Deals", end: true },
    { path: "/admin/deals/last-minute-review", label: "Last-Minute Review" },
    { path: "/admin/deals/gallery-review", label: "Gallery Review" },
    { path: "/admin/deals/stuck-deals", label: "Stuck Deals" },
    { path: "/admin/deals/deleted-by-seller", label: "Deleted by Seller" },
    { path: "/admin/deals/indexing", label: "Indexing" },
  ],
  channels: [
    { path: "/admin/channels/notifications", label: "Notifications" },
    { path: "/admin/channels/notification-ops", label: "Notification Ops" },
    { path: "/admin/channels/whatsapp", label: "WhatsApp" },
    { path: "/admin/channels/voice-agent", label: "Voice Agent" },
    { path: "/admin/channels/email", label: "Email" },
  ],
  billing: [
    { path: "/admin/billing/plans", label: "Plans & Prices" },
    { path: "/admin/billing/coupons", label: "Coupons & Discounts" },
    { path: "/admin/billing/invoices", label: "Invoices" },
    { path: "/admin/billing/invoice-design", label: "Invoice design" },
    { path: "/admin/billing/limits", label: "Plan Limits" },
    { path: "/admin/billing/pricing", label: "Credits & Features" },
    { path: "/admin/billing/payment-gateway", label: "Payment Gateway" },
    { path: "/admin/billing/subscriptions", label: "Subscribers" },
    { path: "/admin/billing/boost", label: "Boost Campaigns" },
  ],
  storefront: [
    { path: "/admin/storefront/categories", label: "Categories" },
    { path: "/admin/storefront/home-deal-banners", label: "Home Deal Banners" },
    { path: "/admin/storefront/chips", label: "Suggestion Chips" },
    { path: "/admin/storefront/promo-banners", label: "Promotional Banners" },
  ],
  content: [
    { path: "/admin/content/news", label: "News" },
    { path: "/admin/content/webinars", label: "Webinars" },
    { path: "/admin/content/polls", label: "Polls & QnA" },
    { path: "/admin/content/community", label: "Community" },
  ],
  aiConfig: [
    { path: "/admin/ai-config/prompts", label: "Prompt Playground" },
    { path: "/admin/ai-config/templates", label: "Templates" },
    { path: "/admin/ai-config/recommendations", label: "Rec Engine" },
  ],
  settings: [
    { path: "/admin/settings", label: "Settings", end: true },
    { path: "/admin/settings/onboarding", label: "Onboarding" },
    { path: "/admin/settings/legal", label: "Legal & Compliance" },
  ],
} satisfies Record<string, HubTab[]>;

/** Horizontal sub-tab strip for an admin hub page; renders the active sub-tab
 *  route underneath.
 *
 *  The strip scrolls horizontally on narrow screens. A bare `overflow-x-auto`
 *  gives no hint that there is more past the edge (the trailing tabs just look
 *  clipped), so the ends are masked with a short fade that only shows on the
 *  side that actually has more content. The negative margins match the admin
 *  layout's responsive padding (`p-4 sm:p-6 lg:p-8`) so the strip bleeds to the
 *  viewport edge at every breakpoint instead of overflowing by 8px on mobile. */
const AdminHubTabs: React.FC<{ tabs: HubTab[] }> = ({ tabs }) => {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ left: false, right: false });

  const syncEdges = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setEdges({ left: el.scrollLeft > 1, right: el.scrollLeft < max - 1 });
  }, []);

  useEffect(() => {
    syncEdges();
    window.addEventListener("resize", syncEdges);
    return () => window.removeEventListener("resize", syncEdges);
  }, [syncEdges, tabs]);

  return (
    <div>
      <div className="relative -mx-4 sm:-mx-6 lg:-mx-8 -mt-4 sm:-mt-6 lg:-mt-8 mb-6 border-b border-gray-800">
        <div
          ref={scrollerRef}
          onScroll={syncEdges}
          className="overflow-x-auto px-4 sm:px-6 lg:px-8"
        >
          <nav className="flex gap-1 min-w-max">
            {tabs.map((tab) => (
              <NavLink
                key={tab.path}
                to={tab.path}
                end={tab.end}
                className={({ isActive }) =>
                  `px-4 py-3 -mb-px text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                    isActive
                      ? "border-indigo-400 text-indigo-400"
                      : "border-transparent text-gray-400 hover:text-white"
                  }`
                }
              >
                {tab.label}
              </NavLink>
            ))}
          </nav>
        </div>
        <div
          aria-hidden
          className={`pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-gray-950 to-transparent transition-opacity duration-200 ${
            edges.left ? "opacity-100" : "opacity-0"
          }`}
        />
        <div
          aria-hidden
          className={`pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-gray-950 to-transparent transition-opacity duration-200 ${
            edges.right ? "opacity-100" : "opacity-0"
          }`}
        />
      </div>
      <Outlet />
    </div>
  );
};

export default AdminHubTabs;
